// frontend/src/components/athleteEditor/useMeasurableSaves.js
// Shared save/verify logic for measurable & gear fields, ported verbatim from
// MeasurablesEditor.jsx's commitField/toggleVerify (same endpoints, same
// payloads, same optimistic-set + revert-on-error behavior) so both
// MeasurablesCard (Task 4) and the ledger (Task 6) drive the exact same
// brain instead of duplicating it.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { latestVerifierFor } from '../../lib/athleteEditorModel';

const hasValue = v => v !== null && v !== undefined && v !== '';

export default function useMeasurableSaves({ userId, profile, apiUrl, getConfig, logs, onChange, onSaved }) {
  const safe = profile || {};
  const [values, setValues] = useState(() => ({ ...safe }));
  const [errors, setErrors] = useState({});

  // Re-sync local editable values whenever the parent hands us a fresh
  // profile (initial load, athlete switch, or after it merges our own
  // onChange back in) — the prop is our source of truth for "last saved".
  useEffect(() => {
    setValues({ ...(profile || {}) });
  }, [profile]);

  // Mirrors VerifyPlayerStatsPage.js's updateStat() (POST .../update-stat,
  // { stat_name, value }) for catalog stat fields, and PlayerStatsPage.js's
  // saveFlat() (POST .../profile with { as_athlete_id }) for gear/shoe_size.
  const commitField = async (field, value, isProfileField) => {
    const prev = safe[field];
    setValues(v => ({ ...v, [field]: value }));
    setErrors(e => ({ ...e, [field]: null }));
    try {
      if (isProfileField) {
        await axios.post(`${apiUrl}/athletes/profile`, { [field]: value }, {
          ...getConfig(),
          params: { as_athlete_id: userId },
        });
      } else {
        await axios.post(`${apiUrl}/athletes/${userId}/update-stat`, { stat_name: field, value }, getConfig());
      }
      onChange && onChange({ [field]: value });
      onSaved && onSaved();
    } catch {
      setValues(v => ({ ...v, [field]: prev }));
      setErrors(e => ({ ...e, [field]: 'Save failed — please try again.' }));
    }
  };

  // Mirrors VerifyPlayerStatsPage.js's toggleVerification() exactly:
  // POST .../{userId}/verify with { stat, verified }.
  const toggleVerify = async (field) => {
    const vf = `${field}_verified`;
    const prev = !!safe[vf];
    const next = !prev;
    setValues(v => ({ ...v, [vf]: next }));
    setErrors(e => ({ ...e, [field]: null }));
    try {
      await axios.post(`${apiUrl}/athletes/${userId}/verify`, { stat: field, verified: next }, getConfig());
      onChange && onChange({ [vf]: next });
      onSaved && onSaved();
    } catch {
      setValues(v => ({ ...v, [vf]: prev }));
      setErrors(e => ({ ...e, [field]: 'Verify update failed.' }));
    }
  };

  const fieldState = (key) => !hasValue(values[key]) ? 'empty' : (values[key + '_verified'] ? 'verified' : 'value');
  const sealState = (key) => !hasValue(values[key]) ? 'none' : (values[key + '_verified'] ? 'verified' : 'unverified');
  const sealTitle = (key) => {
    if (values[key + '_verified']) {
      const v = latestVerifierFor(logs, key);
      return v ? `Verified by ${v.name} · ${v.date} — click to unverify` : 'Verified — click to unverify';
    }
    return 'Not verified — click to verify';
  };

  return { values, setValues, errors, commitField, toggleVerify, fieldState, sealState, sealTitle };
}
